import { useEffect, useLayoutEffect, useRef } from 'react'
import type { RefObject } from 'react'

export function useNotesFeedScroll(noteIds: readonly string[]): {
  feedRef: RefObject<HTMLElement>
  onScroll: () => void
} {
  const feedRef = useRef<HTMLElement>(null)
  const previous = useRef<{ first?: string; last?: string }>({})
  const anchor = useRef<{ element: HTMLElement; top: number } | null>(null)

  const onScroll = (): void => {
    const feed = feedRef.current
    const notes = feed?.querySelectorAll<HTMLElement>(
      ':scope > ul > li:not([data-notes-divider])',
    )
    const feedBounds = feed?.getBoundingClientRect()
    const element =
      Array.from(notes ?? []).find((note) => {
        const bounds = note.getBoundingClientRect()
        return (
          feedBounds &&
          bounds.bottom > feedBounds.top &&
          bounds.top < feedBounds.bottom
        )
      }) ?? notes?.[0]
    anchor.current = element
      ? { element, top: element.getBoundingClientRect().top }
      : null
  }

  useLayoutEffect(() => {
    const feed = feedRef.current
    if (!feed) return

    const first = noteIds[0]
    const last = noteIds.at(-1)
    const before = previous.current
    const changedPost =
      before.first &&
      before.last &&
      !noteIds.includes(before.first) &&
      !noteIds.includes(before.last)
    if (
      last &&
      (!before.last ||
        changedPost ||
        (last !== before.last && noteIds.includes(before.last)))
    ) {
      feed.scrollTop = feed.scrollHeight
    } else if (anchor.current && feed.contains(anchor.current.element)) {
      feed.scrollTop +=
        anchor.current.element.getBoundingClientRect().top - anchor.current.top
    }
    previous.current = { first, last }
    onScroll()
  })

  // The composer growing shrinks the feed. A feed read to the end stays there;
  // one scrolled up keeps its place. Whether it was at the end is judged
  // against the viewport before this change, since the pin itself moves
  // scrollTop a frame at a time while the composer animates.
  useEffect(() => {
    const feed = feedRef.current
    if (!feed || typeof ResizeObserver === 'undefined') return
    let viewport = feed.clientHeight
    const observer = new ResizeObserver(() => {
      const wasAtEnd = feed.scrollHeight - feed.scrollTop - viewport <= 1
      viewport = feed.clientHeight
      if (wasAtEnd) feed.scrollTop = feed.scrollHeight
    })
    observer.observe(feed)
    return (): void => observer.disconnect()
  }, [])

  return { feedRef, onScroll }
}
